/**
 * Where somebody was going when they were asked to sign in or sign up.
 *
 * Signup is product-neutral: an account is a CenoX identity, not a business.
 * What decides where a new account lands is the intent the person arrived
 * with — `/automation`, `/ai`, `/business` — carried as `?next=` through
 * sign-in and registration, and CenoX Home when there was none.
 *
 * A query parameter rather than router state, so it survives a page reload and
 * the hop between the sign-in and registration screens.
 */

import type { MembershipSummary } from '@cenox/shared';

export const CENOX_HOME = '/home';

/**
 * A `next` value that is safe to navigate to.
 *
 * Same-origin paths only. `//evil.example` is a protocol-relative URL to
 * another host, and a backslash is normalised into one by some browsers — an
 * open redirect from the sign-in page is a phishing kit somebody else builds.
 * The auth screens themselves are refused too, so a link can never send a
 * person who just signed in back to the form they just filled.
 */
export function safeNext(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return null;
  const path = value.split(/[?#]/)[0] ?? '';
  if (['/login', '/register', '/forgot-password'].includes(path)) return null;
  // A location sign-in is an auth screen too: `/l/<code>` is the form the
  // worker has just filled, and returning them to it is a loop.
  if (path === '/l' || path.startsWith('/l/')) return null;
  return value;
}

/** Read `?next=` from a location's search string. */
export function nextFrom(search: string): string | null {
  return safeNext(new URLSearchParams(search).get('next'));
}

/** Append `?next=` to an auth screen's path, when there is somewhere to return to. */
export function withNext(path: string, next: string | null): string {
  return next ? `${path}?next=${encodeURIComponent(next)}` : path;
}

/**
 * The workspaces that hold CenoX Business.
 *
 * A `platform` workspace is one created for another product, with no shop in
 * it. Belonging to one says nothing about whether somebody has a business, so
 * it must not open the Business shell — and must not stop them creating one.
 */
export function businessMemberships(memberships: MembershipSummary[]): MembershipSummary[] {
  return memberships.filter((m) => m.businessType !== 'platform');
}
